import { round } from 'lodash';
import getFundValueBridgeXAxisHeight from '../x-axis/get-x-axis-height';
import {
  LABEL_FONT_SIZE,
  LABEL_HORIZONTAL_PADDING,
  LABEL_LINE_HEIGHT,
  LABEL_MAX_LINES,
  VALUE_FONT_SIZE,
  VALUE_ROW_LINE_HEIGHT,
  VALUE_TO_LABEL_GAP,
} from '../x-axis/constants';
import {
  PDF_LABEL_FONT_SIZE,
  PDF_LABEL_SCALE,
  scalePdfLayoutMetric,
} from './pdf-label-typography';
import {
  AXIS_LABEL_FONT_SIZE,
  BAR_WIDTH,
  BAR_WIDTH_NARROW,
  BRIDGE_BAR_COLOR,
  CHART_HEIGHT,
  CHART_TOP_MARGIN,
  MIN_COLUMN_SLOT_WIDTH,
  MIN_COLUMN_SLOT_WIDTH_NARROW,
  PDF_BAR_WIDTH_RATIO,
  Y_AXIS_CHAR_WIDTH,
  Y_AXIS_GRID_GAP,
  Y_AXIS_LABEL_EXTRA_WIDTH,
  Y_AXIS_PADDING,
} from './constants';

type FundValueBridgeChartVariant = 'portal' | 'pdf';

const PDF_CHART_HEIGHT = 295;

export const PDF_CHART_BITMAP_SCALE = 8;

const PDF_CHART_PADDING_LEFT = 2;
const PDF_PAGE_MAX_CONTENT_WIDTH = 595 - 24 * 2;
const PORTAL_GRID_LINE_STROKE_WIDTH = 1;
const PORTAL_REFERENCE_LINE_STROKE_WIDTH = 1;
const PDF_GRID_LINE_STROKE_WIDTH = 1 / PDF_CHART_BITMAP_SCALE;
const PDF_REFERENCE_LINE_STROKE_WIDTH = 0.5;
const PDF_VALUE_TO_LABEL_GAP = 12;
const PDF_LAYOUT_SCALE = PDF_CHART_HEIGHT / CHART_HEIGHT;

const getMinChartContentWidth = (columnCount: number) =>
  round(MIN_COLUMN_SLOT_WIDTH_NARROW * columnCount, 2);

const resolvePortalBarWidth = (columnSlotWidth: number) => {
  if (columnSlotWidth >= MIN_COLUMN_SLOT_WIDTH) {
    return BAR_WIDTH;
  }

  return BAR_WIDTH_NARROW;
};

const getXAxisHeight = (
  valueToLabelGap: number,
  labelFontSize: number,
  labelLineHeight: number,
  labelMaxLines: number,
) => {
  const labelAreaHeight = round(
    labelFontSize * labelLineHeight * labelMaxLines,
    2,
  );

  return round(valueToLabelGap + labelAreaHeight, 2);
};

const scalePdfLayout = () => ({
  chartTopMargin: scalePdfLayoutMetric(CHART_TOP_MARGIN),
  yAxisGridGap: scalePdfLayoutMetric(Y_AXIS_GRID_GAP),
  yAxisCharWidth: Y_AXIS_CHAR_WIDTH * PDF_LABEL_SCALE,
  yAxisLabelExtraWidth: scalePdfLayoutMetric(Y_AXIS_LABEL_EXTRA_WIDTH),
  labelHorizontalPadding: scalePdfLayoutMetric(LABEL_HORIZONTAL_PADDING),
});

const resolvePdfBarWidth = (columnSlotWidth: number) => {
  const referenceScaledSlotWidth = round(
    MIN_COLUMN_SLOT_WIDTH * PDF_LAYOUT_SCALE,
    2,
  );
  const barSlotWidth = Math.min(columnSlotWidth, referenceScaledSlotWidth);

  return Math.max(6, Math.floor(barSlotWidth * PDF_BAR_WIDTH_RATIO));
};

const resolveBarWidth = ({
  variant,
  columnSlotWidth,
}: {
  variant: FundValueBridgeChartVariant;
  columnSlotWidth: number;
}) => {
  if (variant === 'portal') {
    return resolvePortalBarWidth(columnSlotWidth);
  }

  return resolvePdfBarWidth(columnSlotWidth);
};

type FundValueBridgeChartLayout = {
  variant: FundValueBridgeChartVariant;
  barWidth: number;
  columnSlotWidth: number;
  chartContentWidth: number;
  chartWidth: number;
  yAxisWidth: number;
  chartHeight: number;
  chartPaddingLeft: number;
  chartTopMargin: number;
  plotBottom: number;
  xAxisHeight: number;
  axisLabelFontSize: number;
  valueFontSize: number;
  labelFontSize: number;
  labelHorizontalPadding: number;
  labelLineHeight: number;
  labelMaxLines: number;
  valueRowLineHeight: number;
  valueToLabelGap: number;
  yAxisGridGap: number;
  yAxisCharWidth: number;
  yAxisPadding: number;
  yAxisLabelExtraWidth: number;
  bridgeBarColor: string;
  gridLineThickness: number;
  referenceLineStrokeWidth: number;
};

const measureYAxisWidth = (
  tickLabels: Array<string>,
  variant: FundValueBridgeChartVariant,
  columnScale = 1,
) => {
  const scale = variant === 'pdf' ? columnScale : 1;
  const yAxisGridGap = round(Y_AXIS_GRID_GAP * scale, 2);
  const yAxisCharWidth = Y_AXIS_CHAR_WIDTH * scale;
  const yAxisLabelExtraWidth = round(Y_AXIS_LABEL_EXTRA_WIDTH * scale, 2);

  return (
    yAxisGridGap +
    Math.ceil(
      Math.max(...tickLabels.map((label) => label.length), 1) * yAxisCharWidth,
    ) +
    Y_AXIS_PADDING +
    yAxisLabelExtraWidth
  );
};

const buildPortalLayout = ({
  columnCount,
  yAxisTickLabels,
  availableChartWidth,
}: {
  columnCount: number;
  yAxisTickLabels: Array<string>;
  availableChartWidth?: number;
}): FundValueBridgeChartLayout => {
  const xAxisHeight = getFundValueBridgeXAxisHeight();
  const yAxisWidth = measureYAxisWidth(yAxisTickLabels, 'portal');
  const minChartContentWidth = getMinChartContentWidth(columnCount);
  const chartContentWidth =
    availableChartWidth === undefined
      ? minChartContentWidth
      : Math.max(
          minChartContentWidth,
          round(availableChartWidth - yAxisWidth, 2),
        );
  const columnSlotWidth = round(chartContentWidth / columnCount, 2);
  const barWidth = resolvePortalBarWidth(columnSlotWidth);
  const chartWidth = round(chartContentWidth + yAxisWidth, 2);

  return {
    variant: 'portal',
    barWidth,
    columnSlotWidth,
    chartContentWidth,
    chartWidth,
    yAxisWidth,
    chartHeight: CHART_HEIGHT,
    chartPaddingLeft: 0,
    chartTopMargin: CHART_TOP_MARGIN,
    plotBottom: CHART_HEIGHT - xAxisHeight,
    xAxisHeight,
    axisLabelFontSize: AXIS_LABEL_FONT_SIZE,
    valueFontSize: VALUE_FONT_SIZE,
    labelFontSize: LABEL_FONT_SIZE,
    labelHorizontalPadding: LABEL_HORIZONTAL_PADDING,
    labelLineHeight: LABEL_LINE_HEIGHT,
    labelMaxLines: LABEL_MAX_LINES,
    valueRowLineHeight: VALUE_ROW_LINE_HEIGHT,
    valueToLabelGap: VALUE_TO_LABEL_GAP,
    yAxisGridGap: Y_AXIS_GRID_GAP,
    yAxisCharWidth: Y_AXIS_CHAR_WIDTH,
    yAxisPadding: Y_AXIS_PADDING,
    yAxisLabelExtraWidth: Y_AXIS_LABEL_EXTRA_WIDTH,
    bridgeBarColor: BRIDGE_BAR_COLOR,
    gridLineThickness: PORTAL_GRID_LINE_STROKE_WIDTH,
    referenceLineStrokeWidth: PORTAL_REFERENCE_LINE_STROKE_WIDTH,
  };
};

const buildPdfLayout = ({
  columnCount,
  yAxisTickLabels,
}: {
  columnCount: number;
  yAxisTickLabels: Array<string>;
}): FundValueBridgeChartLayout => {
  const layout = scalePdfLayout();
  const yAxisWidth = measureYAxisWidth(yAxisTickLabels, 'pdf', PDF_LABEL_SCALE);
  const chartContentWidth = round(
    PDF_PAGE_MAX_CONTENT_WIDTH - PDF_CHART_PADDING_LEFT - yAxisWidth,
    2,
  );
  const columnSlotWidth = round(chartContentWidth / columnCount, 2);
  const barWidth = resolveBarWidth({
    variant: 'pdf',
    columnSlotWidth,
  });
  const xAxisHeight = getXAxisHeight(
    PDF_VALUE_TO_LABEL_GAP,
    PDF_LABEL_FONT_SIZE,
    LABEL_LINE_HEIGHT,
    LABEL_MAX_LINES,
  );
  const chartWidth = round(
    PDF_CHART_PADDING_LEFT + chartContentWidth + yAxisWidth,
    2,
  );

  return {
    variant: 'pdf',
    barWidth,
    columnSlotWidth,
    chartContentWidth,
    chartWidth,
    yAxisWidth,
    chartHeight: PDF_CHART_HEIGHT,
    chartPaddingLeft: PDF_CHART_PADDING_LEFT,
    chartTopMargin: layout.chartTopMargin,
    plotBottom: PDF_CHART_HEIGHT - xAxisHeight,
    xAxisHeight,
    axisLabelFontSize: PDF_LABEL_FONT_SIZE,
    valueFontSize: PDF_LABEL_FONT_SIZE,
    labelFontSize: PDF_LABEL_FONT_SIZE,
    labelHorizontalPadding: layout.labelHorizontalPadding,
    labelLineHeight: LABEL_LINE_HEIGHT,
    labelMaxLines: LABEL_MAX_LINES,
    valueRowLineHeight: VALUE_ROW_LINE_HEIGHT,
    valueToLabelGap: PDF_VALUE_TO_LABEL_GAP,
    yAxisGridGap: layout.yAxisGridGap,
    yAxisCharWidth: layout.yAxisCharWidth,
    yAxisPadding: Y_AXIS_PADDING,
    yAxisLabelExtraWidth: layout.yAxisLabelExtraWidth,
    bridgeBarColor: BRIDGE_BAR_COLOR,
    gridLineThickness: PDF_GRID_LINE_STROKE_WIDTH,
    referenceLineStrokeWidth: PDF_REFERENCE_LINE_STROKE_WIDTH,
  };
};

export const buildFundValueBridgeChartLayout = ({
  variant,
  columnCount,
  yAxisTickLabels,
  availableChartWidth,
}: {
  variant: FundValueBridgeChartVariant;
  columnCount: number;
  yAxisTickLabels: Array<string>;
  availableChartWidth?: number;
}): FundValueBridgeChartLayout => {
  if (variant === 'portal') {
    return buildPortalLayout({
      columnCount,
      yAxisTickLabels,
      availableChartWidth,
    });
  }

  return buildPdfLayout({ columnCount, yAxisTickLabels });
};
